import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';

export interface ExtractedPdfResult {
  suggestedTitle: string;
  htmlContent: string;
  pageCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PdfExtractorService {
  constructor() {
    if (typeof window !== 'undefined') {
      try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs';
      } catch (e) {
        console.warn('Could not initialize PDF.js workerSrc:', e);
      }
    }
  }

  async extractPdf(file: File, onProgress?: (percent: number, message: string) => void): Promise<ExtractedPdfResult> {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDoc = await loadingTask.promise;
    const numPages = pdfDoc.numPages;

    let allHtmlPages: string[] = [];
    let detectedTitle = '';

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      if (onProgress) {
        const percent = Math.round((pageNum / numPages) * 100);
        onProgress(percent, `Membaca halaman ${pageNum} dari ${numPages}...`);
      }

      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageHtml = this.parseTextContentToHtml(textContent.items, pageNum === 1);

      if (pageNum === 1 && !detectedTitle) {
        detectedTitle = this.extractTitleCandidate(textContent.items, file.name);
      }

      if (pageHtml.trim()) {
        allHtmlPages.push(pageHtml);
      }
    }

    const htmlContent = allHtmlPages.join('<br/><hr/><br/>');
    const cleanFileName = file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ');
    const suggestedTitle = detectedTitle || cleanFileName;

    return {
      suggestedTitle,
      htmlContent,
      pageCount: numPages
    };
  }

  private parseTextContentToHtml(items: any[], isFirstPage: boolean): string {
    if (!items || items.length === 0) return '';

    // Collect valid text items with geometry
    const textItems: Array<{ str: string; x: number; y: number; height: number }> = [];

    for (const item of items) {
      if ('str' in item && item.str.trim().length > 0) {
        const x = item.transform ? item.transform[4] : 0;
        const y = item.transform ? item.transform[5] : 0;
        const height = item.height || (item.transform ? Math.abs(item.transform[0]) : 12);
        textItems.push({ str: item.str, x, y, height });
      }
    }

    if (textItems.length === 0) return '';

    // Calculate median font height to distinguish headings from body text
    const heights = textItems.map(t => t.height).sort((a, b) => a - b);
    const medianHeight = heights[Math.floor(heights.length / 2)] || 12;

    // Group items into lines (items with roughly the same Y within 3px tolerance)
    textItems.sort((a, b) => {
      if (Math.abs(a.y - b.y) <= 3) {
        return a.x - b.x;
      }
      return b.y - a.y; // Top to bottom
    });

    const lines: Array<{ text: string; height: number; y: number }> = [];
    let currentLine: { text: string; height: number; y: number } | null = null;

    for (const item of textItems) {
      if (!currentLine) {
        currentLine = { text: item.str, height: item.height, y: item.y };
      } else if (Math.abs(currentLine.y - item.y) <= 3) {
        currentLine.text += (currentLine.text.endsWith(' ') || item.str.startsWith(' ') ? '' : ' ') + item.str;
        currentLine.height = Math.max(currentLine.height, item.height);
      } else {
        lines.push(currentLine);
        currentLine = { text: item.str, height: item.height, y: item.y };
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    // Convert lines into HTML blocks (paragraphs / headings)
    const blocks: string[] = [];
    let currentParagraph: string[] = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        blocks.push(`<p>${currentParagraph.join(' ')}</p>`);
        currentParagraph = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.text.trim();
      if (!trimmed) continue;

      const isHeading = line.height > medianHeight * 1.3 && trimmed.length < 120;
      const isListItem = /^[•\-\*]\s+|^\d+[\.\)]\s+/.test(trimmed);

      if (isHeading) {
        flushParagraph();
        blocks.push(`<h3><strong>${this.escapeHtml(trimmed)}</strong></h3>`);
      } else if (isListItem) {
        flushParagraph();
        blocks.push(`<p>• ${this.escapeHtml(trimmed.replace(/^[•\-\*]\s+/, ''))}</p>`);
      } else {
        currentParagraph.push(this.escapeHtml(trimmed));

        // Check vertical gap with next line for paragraph break
        if (i < lines.length - 1) {
          const gap = Math.abs(line.y - lines[i + 1].y);
          if (gap > line.height * 1.8) {
            flushParagraph();
          }
        }
      }
    }
    flushParagraph();

    return blocks.join('');
  }

  private extractTitleCandidate(items: any[], fallbackFileName: string): string {
    let maxItem: { str: string; height: number } | null = null;
    for (const item of items) {
      if ('str' in item && item.str.trim().length > 3) {
        const height = item.height || (item.transform ? Math.abs(item.transform[0]) : 12);
        if (!maxItem || height > maxItem.height) {
          maxItem = { str: item.str.trim(), height };
        }
      }
    }
    if (maxItem && maxItem.str.length > 3 && maxItem.str.length < 150) {
      return maxItem.str;
    }
    return fallbackFileName.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ');
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
